import { Permission } from '@act/shared/authorization';
import {
  MemberStatusDto,
  MemberStatusSelectOption,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { isOutOfScopeStatusChangeRestricted } from '@act/shared/types';
import { createSelector } from 'reselect';
import { useAppSelector } from '../storeHooks';
import type { RootState } from '../reemaStore.util';
import { selectClientById } from './clientSlice.slice';
import { checkPermissionOnMember } from 'redux/utils/auth/permissionChecks';
import { selectSelectedMemberId } from './selectedMemberSlice.slice';
import {
  memberEntityAdapter,
  memberForMapEntityAdapter,
  memberStatusEntityAdapter,
  paginatedMemberEntityAdapter,
} from './memberSliceState.util';
import type { initialMemberState } from './memberSliceState.util';

interface MemberSliceSelectorProps {
  guideId?: string;
  memberStageId?: number;
  memberStatusId?: number;
  [key: string]: unknown;
}

const selectMembersSliceState = state =>
  (state as { members: typeof initialMemberState })?.members;

export const selectMembersState = state =>
  (state as { members: typeof initialMemberState })?.members?.members;

const selectMemberStatusesState = state =>
  (state as { members: typeof initialMemberState })?.members?.memberStatuses;

const selectPaginatedMembersState = state =>
  (state as { members: typeof initialMemberState })?.members?.paginatedMembers;

const selectMembersForMapState = state =>
  (state as { members: typeof initialMemberState })?.members?.membersForMap;

const selectPaginatedMemberCount = createSelector(
  selectMembersSliceState,
  membersState => membersState.count
);

const selectSliceStatus = createSelector(
  selectMembersSliceState,
  membersState => membersState.status
);

export const { selectAll: selectAllMembers, selectTotal: selectMembersTotal } =
  memberEntityAdapter.getSelectors(selectMembersState);

export const {
  selectAll: selectAllPaginatedMembers,
  selectTotal: selectPaginatedMembersTotal,
} = paginatedMemberEntityAdapter.getSelectors(selectPaginatedMembersState);

export const {
  selectAll: selectAllMembersForMap,
  selectTotal: selectedTotalMembersForMap,
} = memberForMapEntityAdapter.getSelectors(selectMembersForMapState);

const { selectAll: selectAllMemberStatuses } =
  memberStatusEntityAdapter.getSelectors(selectMemberStatusesState);

const selectMembersMapByInteractableId = createSelector(
  selectAllMembers,
  allMembers => {
    const map: { [key: string]: UserDto } = {};
    allMembers.forEach(m => {
      map[m.interactableId] = m;
    });
    return map;
  }
);

const selectMemberStatusById = (id: number) =>
  createSelector(selectMemberStatusesState, state => {
    return state.entities[id];
  });

export const selectMemberById = (id: string) =>
  createSelector(selectMembersState, members => {
    return members?.entities?.[id];
  });

export const selectSelectedMember = state => {
  const selectedMemberId = selectSelectedMemberId(state);
  return selectMemberById(selectedMemberId)(state);
};

interface SelectableStatusesSelectorProps {
  memberId?: string;
}

export const selectSelectableMemberStatuses: (
  state,
  props: SelectableStatusesSelectorProps
) => MemberStatusSelectOption[] = (state, props) => {
  const memberStatuses = selectAllMemberStatuses(state);
  const member = selectMemberById(props.memberId)(state);
  const client = selectClientById(member?.clientId)(state);
  const isBhClient = !!client?.avonMedicalCenterId;

  const calculateIsSelectable = (memberStatus: MemberStatusDto) => {
    /*
     * Out of scope is terminal for non-admin status requests. Admins still
     * see every option via CAN_REQUEST_ADMIN_LEVEL_STATUSES.
     */
    if (isOutOfScopeStatusChangeRestricted(member?.memberStatus?.key)) {
      return false;
    }
    const isSelectableFromCurrentStage =
      memberStatus.changeRequestStageIds?.includes(member?.memberStage?.id);
    const isSelectableFromCurrentStatus =
      memberStatus.changeRequestStatusIds?.includes(
        member?.memberStatus?.memberStatusId
      );
    const isSelectableFromBhClient =
      isBhClient && memberStatus.key === 'skill-building';
    return (
      isSelectableFromCurrentStage ||
      isSelectableFromCurrentStatus ||
      isSelectableFromBhClient
    );
  };

  return memberStatuses.map(memberStatus => {
    return {
      isSelectable: calculateIsSelectable(memberStatus),
      memberStatus,
    };
  });
};

export const selectMembersMap = createSelector(selectMembersState, members => {
  return members.entities;
});

/**
 * Returns member ids from the input list that are not yet loaded in the member slice.
 * @param state Redux root state.
 * @param memberIds Member ids to check against the loaded member map.
 * @returns Unique member ids that are missing from the member slice.
 */
export const selectMissingMemberIds = (
  state: RootState,
  memberIds: string[]
): string[] => {
  const membersMap = selectMembersMap(state);
  const uniqueIds = [...new Set(memberIds.filter(Boolean))];
  return uniqueIds.filter(id => !membersMap[id]);
};

export const hasPermissionOnMember = (
  state: RootState,
  memberId: string,
  permission: Permission
) => {
  const member: UserDto = selectMemberById(memberId)(state);
  const actingUser = state.actingUser.user;
  return checkPermissionOnMember({ actingUser, member, permission });
};

export const selectMembersByAssignedGuide: (
  state,
  props: MemberSliceSelectorProps
) => UserDto[] = createSelector(
  [selectAllMembers, (state, props: MemberSliceSelectorProps) => props.guideId],
  (members, guideId) =>
    members.filter(member => member.assignedGuideId === guideId)
);

export const membersAppSelectors = {
  useSelectAllMembers: () => useAppSelector(selectAllMembers),
  useSelectMembersTotal: () => useAppSelector(selectMembersTotal),
  useSelectMembersMap: () => useAppSelector(selectMembersMap),
  useSelectAllPaginatedMembers: () => useAppSelector(selectAllPaginatedMembers),
  useSelectTotalPaginatedMembers: () =>
    useAppSelector(selectPaginatedMembersTotal),
  useSelectAllMembersForMap: () => useAppSelector(selectAllMembersForMap),
  useSelectedTotalMembersForMap: () =>
    useAppSelector(selectedTotalMembersForMap),
  useSelectSliceStatus: () => useAppSelector(selectSliceStatus),
  useSelectPaginatedMemberCount: () =>
    useAppSelector(selectPaginatedMemberCount),
  useSelectAllMemberStatuses: () => useAppSelector(selectAllMemberStatuses),
  useSelectMemberStatusById: (id: number) =>
    useAppSelector(selectMemberStatusById(id)),
  useSelectMemberById: id => useAppSelector(selectMemberById(id)),
  useSelectMemberByInteractableId: (id: string) =>
    useAppSelector(state => selectMembersMapByInteractableId(state)[id]),
  useSelectMembersByAssignedGuide: (id: string) =>
    useAppSelector(state =>
      selectMembersByAssignedGuide(state, { guideId: id })
    ),
  useSelectSelectableMemberStatuses: (input: SelectableStatusesSelectorProps) =>
    useAppSelector(state => selectSelectableMemberStatuses(state, input)),
  useHasPermissionOnMember: (memberId: string, permission: Permission) =>
    useAppSelector(state => hasPermissionOnMember(state, memberId, permission)),
};
